export const ProductTypeEnum = {
  lendingValue: "lendingvalue",
  marketValue: "marketvalue",
} as const;
export type ProductTypeEnum = (typeof ProductTypeEnum)[keyof typeof ProductTypeEnum];

export const ObjectTypeEnum = {
  house: "HOUSE",
  apartment: "APARTMENT",
  multiFamilyHouse: "MULTI_FAMILY_HOUSE",
  mixedUseProperty: "MIXED_USE_PROPERTY",
  plotOfLand: "PLOT_OF_LAND",
} as const;
export type ObjectTypeEnum = (typeof ObjectTypeEnum)[keyof typeof ObjectTypeEnum];

export interface DefaultBuildingData {
  construction?: Construction;
  numberOfFloors?: number;
  unitsNumber?: number;
}

export const getDefaultBuildingData = (objectType?: ObjectTypeEnum): DefaultBuildingData => {
  switch (objectType) {
    case ObjectTypeEnum.apartment:
      return { unitsNumber: 10 };
    case ObjectTypeEnum.house:
      return { construction: Construction.massive, numberOfFloors: 1 };
    case ObjectTypeEnum.multiFamilyHouse:
    case ObjectTypeEnum.mixedUseProperty:
      return { construction: Construction.massive, numberOfFloors: 1, unitsNumber: 10 };
    default:
      return {};
  }
};

export const BuildingTypeEnum = {
  singleFamilyHouse: "SINGLE_FAMILY_HOUSE",
  twoFamilyHouse: "TWO_FAMILY_HOUSE",
  singleFamilyHouseWithGrannyApartment: "SINGLE_FAMILY_HOUSE_WITH_GRANNY_APARTMENT",
  apartment: "APARTMENT",
  multiFamilyHouse: "MULTI_FAMILY_HOUSE",
  mixedUseProperty: "MIXED_USE_PROPERTY",
  plotArea: "PLOT_AREA",
} as const;
export type BuildingTypeEnum = (typeof BuildingTypeEnum)[keyof typeof BuildingTypeEnum];

export const OwnershipEnum = {
  fullOwnership: "FULL",
  leasehold: "LEASE",
  apartmentOwnership: "SHARED",
  leaseholdApartmentOwnership: "SHARED_LEASE",
} as const;
export type OwnershipEnum = (typeof OwnershipEnum)[keyof typeof OwnershipEnum];

export const ActualUseEnum = {
  ownerOccupied: "OWNER_OCCUPIED",
  rented: "RENTED",
  //empty: "EMPTY",
  partialOwnerOccupied: "PARTIALLY_OWNER_OCCUPIED",
} as const;
export type ActualUseEnum = (typeof ActualUseEnum)[keyof typeof ActualUseEnum];

export const HouseOrApartmentTypeEnum = {
  //house
  detachedHouse: "DETACHED_HOUSE",
  semiDetachedHouse: "SEMI_DETACHED_HOUSE",
  endTerraceHouse: "END_TERRACED_HOUSE",
  midTerracedHouse: "MID_TERRACED_HOUSE",
  //apartment
  loft: "LOFT",
  penthouse: "PENTHOUSE",
  basement: "BASEMENT_APARTMENT",
  groundFloorApartment: "GROUND_FLOOR_APARTMENT",
  midFloorApartment: "MID_FLOOR_APARTMENT",
  raisedFirstFloorApartment: "RAISED_FIRST_FLOOR_APARTMENT",
  twoFloorApartment: "TWO_FLOOR_APARTMENT",
  terracedApartment: "TERRACED_APARTMENT",
  atticApartment: "ATTIC_APARTMENT",
  atticPenthouse: "ATTIC_PENTHOUSE",
  apartmentWithGarden: "APARTMENT_WITH_GARDEN",
} as const;

export type HouseOrApartmentTypeEnum = (typeof HouseOrApartmentTypeEnum)[keyof typeof HouseOrApartmentTypeEnum];

export const Construction = {
  massive: "MASSIVE",
  prefabHouseSolid: "PREFABRICATED_MASSIVE_HOUSE",
  prefabHouseWood: "PREFABRICATED_WOOD_HOUSE",
} as const;
export type Construction = (typeof Construction)[keyof typeof Construction];

export const SpecialConstructionEnum = {
  panelConstruction: "PANEL_CONSTRUCTION",
  largeBlockConstruction: "LARGE_BLOCK_CONSTRUCTION",
  terracedHouse: "TERRACED_HOUSE",
  notSpecified: "NOT_SPECIFIED",
} as const;
export type SpecialConstructionEnum = (typeof SpecialConstructionEnum)[keyof typeof SpecialConstructionEnum];

export const FloorPlanTypeEnum = {
  singleAccess: "EINSPAENNER",
  twoAccess: "ZWEISPAENNER",
  threeAccess: "DREISPAENNER",
  fourAccess: "VIERSPAENNER",
  notSpecified: "KEINE_ANGABE",
} as const;
export type FloorPlanTypeEnum = (typeof FloorPlanTypeEnum)[keyof typeof FloorPlanTypeEnum];

export const RoofEnum = {
  full: "FULLY_DEVELOPED",
  partial: "PARTIALLY_DEVELOPED",
  none: "NOT_DEVELOPED",
  flatRoof: "FLAT",
} as const;
export type RoofEnum = (typeof RoofEnum)[keyof typeof RoofEnum];

export const BasementEnum = {
  full: "FULL",
  partial: "PARTIAL",
  none: "NONE",
} as const;
export type BasementEnum = (typeof BasementEnum)[keyof typeof BasementEnum];

export const BasementConversionEnum = {
  full: "FULL",
  partial: "PARTIAL",
  none: "NONE",
} as const;
export type BasementConversionEnum = (typeof BasementConversionEnum)[keyof typeof BasementConversionEnum];

export const OutdoorFacilitiesEnum = {
  none: "NONE",
  belowAverage: "BELOW_AVERAGE",
  average: "AVERAGE",
  aboveAverage: "ABOVE_AVERAGE",
} as const;
export type OutdoorFacilitiesEnum = (typeof OutdoorFacilitiesEnum)[keyof typeof OutdoorFacilitiesEnum];

export const BathroomEnum = {
  inside: "INSIDE",
  moreThanOneBath: "MORE_THAN_ONE_BATH",
  withWindow: "WITH_WINDOW",
  oneBath: "ONE_BATH",
} as const;
export type BathroomEnum = (typeof BathroomEnum)[keyof typeof BathroomEnum];

export const SurfaceMountedInstallationEnum = {
  cablesOnWalls: "CABLES_ON_WALLS",
  cablesNotOnWalls: "CABLES_NOT_ON_WALLS",
} as const;
export type SurfaceMountedInstallationEnum =
  (typeof SurfaceMountedInstallationEnum)[keyof typeof SurfaceMountedInstallationEnum];

export const ExteriorWallsEnum = {
  exteriorWallsInsulated: "AUSSENWAENDE_GEDAEMMT",
  exteriorWallsNotInsulated: "AUSSENWAENDE_NICHT_GEDAEMMT",
} as const;
export type ExteriorWallsEnum = (typeof ExteriorWallsEnum)[keyof typeof ExteriorWallsEnum];

export const HeatingEnum = {
  floorHeating: "FLOOR_HEATING",
  stovesHeating: "STOVES_HEATING",
  centralHeating: "CENTRAL_HEATING",
  other: "OTHER",
} as const;
export type HeatingEnum = (typeof HeatingEnum)[keyof typeof HeatingEnum];

export const HeatPumpEnum = {
  heatPump: "HEAT_PUMP",
  noHeatPump: "NO_HEAT_PUMP",
} as const;
export type HeatPumpEnum = (typeof HeatPumpEnum)[keyof typeof HeatPumpEnum];

export const ResidentialAreaEnum = {
  winterGarden: "WINTER_GARDEN",
  balconyAbove10: "BALCONY_ABOVE_10",
  balconyUnder10: "BALCONY_UNDER_10",
  noBalcony: "NO_BALCONY",
} as const;
export type ResidentialAreaEnum = (typeof ResidentialAreaEnum)[keyof typeof ResidentialAreaEnum];

export const RoofCoveringEnum = {
  roofingFelt: "ROOFING_FELT",
  roofingTiles: "ROOFING_TILES",
  metal: "METAL",
  slate: "SLATE",
  other: "OTHER",
} as const;
export type RoofCoveringEnum = (typeof RoofCoveringEnum)[keyof typeof RoofCoveringEnum];

export const FloorCoveringEnum = {
  tiles: "TILES",
  plasticPVC: "PLASTIC_PVC",
  parquetNaturalStone: "PARQUET_NATURAL_STONE",
  carpetLaminate: "CARPET_LAMINATE",
  other: "OTHER",
} as const;
export type FloorCoveringEnum = (typeof FloorCoveringEnum)[keyof typeof FloorCoveringEnum];

export const WindowsEnum = {
  doubleGlazing: "DOUBLE_GLAZING",
  tripleGlazing: "TRIPLE_GLAZING",
  specialGlazing: "SPECIAL_GLAZING",
  floorToCeilingWindow: "FLOOR_TO_CEILING_GLAZING",
  boxWindow: "BOX_WINDOW",
  simple: "SIMPLE",
  other: "OTHER",
} as const;
export type WindowsEnum = (typeof WindowsEnum)[keyof typeof WindowsEnum];

export const StoreRoomEnum = {
  outside: "OUTSIDE",
  inside: "INSIDE",
  outsideAndInside: "OUTSIDE_AND_INSIDE",
  none: "NONE",
} as const;
export type StoreRoomEnum = (typeof StoreRoomEnum)[keyof typeof StoreRoomEnum];

export const ValueEnum = {
  verySimple: "VERY_SIMPLE",
  simple: "SIMPLE",
  simpleToMedium: "SIMPLE_TO_MEDIUM",
  medium: "MEDIUM",
  mediumToHigh: "MEDIUM_TO_HIGH",
  high: "HIGH",
  highToVeryHigh: "HIGH_TO_VERY_HIGH",
  veryHigh: "VERY_HIGH",
  veryVeryHigh: "VERY_VERY_HIGH",
} as const;
export type ValueEnum = (typeof ValueEnum)[keyof typeof ValueEnum];

export const EnergyCertificateEnum = {
  A_PLUS: "A_PLUS",
  A: "A",
  B: "B",
  C: "C",
  D: "D",
  E: "E",
  F: "F",
  G: "G",
  H: "H",
} as const;
export type EnergyCertificateEnum = (typeof EnergyCertificateEnum)[keyof typeof EnergyCertificateEnum];

export const EnergyEfficiencyHouseStandardEnum = {
  kfWPlus: "KFW_40_PLUS",
  kfW40: "KFW_40",
  passive: "PASSIVE",
  kfW55RefValues: "KFW_55_REF_VAL",
  kfW55: "KFW_55",
  kfW70: "KFW_70",
  kfW85: "KFW_85",
  kfW100: "KFW_100",
  kfW115: "KFW_115",
  historicalBuilding: "KFW_HISTORICAL_BUILDING",
} as const;
export type EnergyEfficiencyHouseStandardEnum =
  (typeof EnergyEfficiencyHouseStandardEnum)[keyof typeof EnergyEfficiencyHouseStandardEnum];

export const EnergyCertificateTypeEnum = {
  demand: "DEMAND",
  consumption: "CONSUMPTION",
} as const;
export type EnergyCertificateTypeEnum = (typeof EnergyCertificateTypeEnum)[keyof typeof EnergyCertificateTypeEnum];

export const BuildingStandardDeterminationMethodEnum = {
  detailed: "DETAILED",
  simple: "SIMPLE",
  automatic: "AUTOMATIC",
} as const;
export type BuildingStandardDeterminationMethodEnum =
  (typeof BuildingStandardDeterminationMethodEnum)[keyof typeof BuildingStandardDeterminationMethodEnum];

export const ModernizationMethodEnum = {
  detailed: "DETAILED",
  simple: "SIMPLE",
  costs: "COSTS",
  automatic: "AUTOMATIC",
} as const;
export type ModernizationMethodEnum = (typeof ModernizationMethodEnum)[keyof typeof ModernizationMethodEnum];

export const StatusEnum = {
  none: "NONE",
  partial: "PARTIAL",
  full: "FULL",
} as const;
export type StatusEnum = (typeof StatusEnum)[keyof typeof StatusEnum];

export const ModernizationClassEnum = {
  none: "NONE",
  light: "LIGHT",
  medium: "MEDIUM",
  most: "MOST",
  full: "FULL",
} as const;
export type ModernizationClassEnum = (typeof ModernizationClassEnum)[keyof typeof ModernizationClassEnum];

export const RentReferenceEnum = {
  euroPerSquareMeter: "EURO_PER_SQUARE_METER",
  euroPerYear: "EURO_PER_YEAR",
} as const;
export type RentReferenceEnum = (typeof RentReferenceEnum)[keyof typeof RentReferenceEnum];
